import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { take } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { TimezoneService, TimeZoneOption } from 'src/app/services/timezone.service';
import { PencilSearchableDropdownComponent } from '../pencil-dropdown/pencil-searchable-dropdown.component';

@UntilDestroy()
@Component({
  selector: 'app-pencil-timezone-picker',
  templateUrl: './pencil-timezone-picker.component.html',
  styleUrls: ['./pencil-timezone-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [PencilSearchableDropdownComponent, TranslateModule, AsyncPipe],
})
export class PencilTimezonePickerComponent implements OnChanges {
  /** Observable that emits the resolved timezone when data is available */
  resolvedTimezone: any = null;

  /** value restored from persistence; primitive string or null */
  @Input() initialTimezoneValue: string | undefined;

  /** Placeholder text for the dropdown */
  @Input() placeholder = 'Search timezone';

  /** Emits when timezone selection changes */
  @Output() valueChange = new EventEmitter<string>();

  constructor(public timezoneService: TimezoneService) {
    // NO-OP
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle timezone resolution when initialTimezoneValue changes
    if (changes['initialTimezoneValue'] && this.initialTimezoneValue) {
      const key = this.initialTimezoneValue;
      this.timezoneService.list$.pipe(take(1), untilDestroyed(this)).subscribe((timeZones) => {
        if (!key) {
          this.resolvedTimezone = undefined;
          return;
        }

        /* 1️⃣ first pass – look for an exact .value match */
        const directMatch = timeZones.find((tz) => tz.value === key);

        /* 2️⃣ second pass – if nothing yet, look in the .group aliases */
        const groupMatch = directMatch ?? timeZones.find((tz) => tz.group?.includes(key));

        this.resolvedTimezone = groupMatch ?? undefined;
      });
    }
  }

  /** Handle timezone selection */
  onTimezoneSelected(option: TimeZoneOption): void {
    this.valueChange.emit(option.value);
  }
}
